import './Autocomplete.css';
import { useTranslation } from "react-i18next";
import { useLayoutEffect, useRef, useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * @template T
 * @param {import('./Autocomplete').AutocompleteProps<T>} props
 */
export function Autocomplete({
  resultsSize = 5,
  searchFn,
  onSelect,
  isLoading = false,
  label = '',
  placeholder = '',
  errorMessage = '',
  toElementFn,
  defaultValue = '',
  isDisabled = false,
  onInputChange,
}) {
  const ENTER_KEY = 13;
  const ARROW_DOWN = 40;
  const ARROW_UP = 38;
  const [userInput, setUserInput] = useState('');
  const [options, setOptions] = useState();
  const [activeOption, setActiveOption] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [, setSuggestionsWidth] = useState('300px');
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const [inputPosition, setInputPosition] = useState({ top: 0, left: 0, width: 0 });
  const divRef = useRef();

  const inputRef = useRef(null);

  const { t } = useTranslation();

  // Update userInput when defaultValue changes (e.g., when item is selected programmatically)
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setUserInput(defaultValue);
    }
  }, [defaultValue])

  function handleClickOutside(event) {
    const suggestionsList = event.target.closest("[data-suggestions-list]");
    const autocompleteWrapper = event.target.closest(".autocompleteInputWrapper");
    if (divRef.current && !divRef.current.contains(event.target) && !suggestionsList && !autocompleteWrapper) {
      setShowSuggestions(false);
      setActiveOption(0);
      setOptions();
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup any pending timeouts
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  useLayoutEffect(() => {
    if (divRef.current.clientWidth > 100) {
      setSuggestionsWidth(divRef.current.clientWidth);
    }
  }, []);

  // Calculate input position for portal positioning
  useEffect(() => {
    if (showSuggestions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setInputPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [showSuggestions]);

  const displayError = useMemo(() => {
    return !!options && options.length === 0 ? t(errorMessage) : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const clearAndSetOptions = (results) => {
    if (!!results && results.length === 0) {
      setOptions([]);
      setShowSuggestions(false);
    } else {
      setShowSuggestions(true);
      setOptions(results);
    }
  }

  const onClick = (input) => {
    if (input !== undefined) {
      setIsSelecting(true);
      setShowSuggestions(false);
      setActiveOption(0);
      setOptions();
      setUserInput(options[input].name);
      onSelect(options[input]);
      // Reset the selecting flag after a short delay
      setTimeout(() => setIsSelecting(false), 100);
    }
  };

  const onChange = (e) => {
    // Don't trigger search if we're in the middle of selecting an option
    if (isSelecting) {
      return;
    }
    
    const input = e.currentTarget.value;
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    setUserInput(input);
    if (onInputChange) {
      onInputChange(input);
    }
    setTypingTimeout(
      setTimeout(() => {
        if (input !== '' && input.length > 1) {
          searchFn(input, resultsSize, clearAndSetOptions);
        } else {
          setOptions([]);
        }
      }, 1000),
    );
  };  

  useEffect(() => {
    if (inputRef.current) {
      // inputRef.current.focus();
    }
  }, [options]); // focus whenever options changes

  const onKeyDown = (e) => {
    if (e.keyCode === ENTER_KEY) {
      if (options && Array.isArray(options) && options.length > 0 && options[activeOption]) {
        setIsSelecting(true);
        setActiveOption(0);
        setOptions([]);
        setUserInput(options[activeOption].name);
        setShowSuggestions(false);
        onSelect(options[activeOption]);
        // Reset the selecting flag after a short delay
        setTimeout(() => setIsSelecting(false), 100);
      }
    } else if (e.keyCode === ARROW_UP) {
      if (options && Array.isArray(options) && options.length > 0) {
        if (activeOption === 0) {
          setActiveOption(options.length - 1);
        } else {
          setActiveOption(activeOption - 1);
        }
      }
    } else if (e.keyCode === ARROW_DOWN) {
      if (options && Array.isArray(options) && options.length > 0) {
        if (activeOption + 1 === options.length) {
          setActiveOption(0);
        } else {
          setActiveOption(activeOption + 1);
        }
      }
    }
  };

  const loader = <div className="loaderInInput" />;

  return (
    <div className="w-full max-w-full">
      <p className="text-sm font-extrabold">{ t(label) }</p>
      <div className={`autocompleteInputWrapper ${showSuggestions ? 'autocompleteInputWrapper--open' : ''}`} ref={divRef}>
        <label>
          <span className="formValue autocomplete">
            <input
              className="p-3 w-full border-gray-200 border rounded bg-card text-foreground focus:outline-none"
              onChange={(v) => onChange(v)}
              placeholder={t(placeholder)}
              value={userInput}
              onKeyDown={onKeyDown}
              disabled={isLoading || isDisabled}
              ref={inputRef}
              data-dd-action-name={'Autocomplete'}
            />
            {isLoading && loader}
          </span>
        </label>
        {showSuggestions && options && Array.isArray(options) && options.length > 0 && ReactDOM.createPortal(
          <ul 
            className="suggestions" 
            style={{ 
              position: 'absolute',
              top: inputPosition.top,
              left: inputPosition.left,
              width: inputPosition.width,
              zIndex: 99999
            }} 
            data-suggestions-list
          >
            {options.map((suggestion, index) => {
              let className;

              if (index === activeOption) {
                className = 'suggestion-active';
              }
              return (
                <li className={className} key={index} onClick={() => onClick(index)}>
                  { toElementFn !== undefined ? toElementFn(suggestion)  : <span className="autocompleteSuggestionText">{suggestion.name}</span> }
                </li>
              );
            })}
          </ul>,
          document.body
        )}
      </div>
      <p className="text-xs text-red-600 my-0">{ displayError }</p>
    </div>
  );
}
